import AppPortGateBootstrap from './AppPortGateBootstrap';
import { appPortZiel, appPortZwischenseite } from './appPortGate';
import { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { BuehnenKennungKontext, BuehnenKontext, ADRESSZEILE_EREIGNIS, OEFFNEN_EREIGNIS } from '../../lib/buehnenAdresszeile';
import { devPortUrl } from '../../lib/devPortUrl';
import { entscheidungAusEigenerKopie } from '../../lib/entscheidungsSpiegel';
import { resolveBrowserPartition } from '../../../shared/browserPartition';
import { detectApp, detectSandkastenApp, getDevPort, isPartnerCui, sandkastenPortAusUrl } from '../../../shared/appDetect';
import { useSandkastenApp } from '../../lib/sandkastenApp';
import AppLoginPanel from './AppLoginPanel';
import AppLoginHilfe from './AppLoginHilfe';
import PartnerSessionPanel from './PartnerSessionPanel';
import PartnerKontoAutoLogin from './PartnerKontoAutoLogin';
import Vorbefuellung from './Vorbefuellung';
import { useKachelProtokoll } from './useKachelProtokoll';
import { useAuth } from '../../contexts/AuthContext';
import { wurdeWiederEingeblendet, type Groesse } from './wiederEingeblendet';
import { meldeBrowserPanelAn } from '../../lib/gemounteteBrowserPanels';
import { erzeugeNachladePlaner, istVoruebergehenderLadefehler, type NachladePlaner, type NachladeZustand } from '../../lib/webviewNachladen';
import { isShadowClient } from '../../lib/shadowClient';
import { meldeBrowserPanelBereit, meldeBrowserPanelFort } from '../../lib/boundaries/browserPanelBesitz';

// Live prod apps (energy/report.werking.tools) send X-Frame-Options: DENY +
// CSP frame-ancestors 'none', which blocks <iframe> embedding in the browser
// panel (the non-Electron render path). nginx mirrors them under
// *-live.partner.werking.tools with those headers stripped and the auth-cookie
// domain rewritten (same parent domain → SameSite cookies keep working).
// Rewrite the iframe src to the proxy host so the panel renders the live app;
// the URL bar still shows the original host, so it stays transparent.
const LIVE_PROXY_HOSTS: Record<string, string> = {
  'energy.werking.tools': 'energy-live.partner.werking.tools',
  'report.werking.tools': 'report-live.partner.werking.tools',
  'engelmann.vercel.app': 'engelmann-live.partner.werking.tools',
};
/** Absolute http(s)-Adresse einer anderen Herkunft als dieses CUI? */
function istFremdeHttpAdresse(u: string): boolean {
  if (!/^https?:\/\//i.test(u)) return false;
  try {
    return new URL(u).origin !== window.location.origin;
  } catch {
    return false;
  }
}

function liveProxyRewrite(u: string): string {
  try {
    // Decision HTML is mirrored in both houses — always use the local copy.
    const kopie = entscheidungAusEigenerKopie(u, window.location);
    if (kopie !== u) return kopie;
    const parsed = new URL(u);
    const mapped = LIVE_PROXY_HOSTS[parsed.hostname];
    if (mapped) {
      parsed.hostname = mapped;
      return parsed.toString();
    }
  } catch {
    // not an absolute URL — leave unchanged
  }
  return u;
}

interface BrowserPanelProps {
  initialUrl?: string;
  panelId?: string;
  onUrlChange?: (url: string) => void;
  // Opt-in session isolation: when true, the webview gets its OWN Electron
  // partition (persist:browser-<panelId>) = its own cookie jar, instead of the
  // shared 'persist:browser' every panel otherwise uses. Needed to run several
  // logins in parallel (e.g. one customer per panel) without them clobbering
  // each other's session — the shared partition means "log one panel in = all
  // panels in". Default stays shared (lighter: the Electron main-process CSP/
  // header hooks are set up once). main.cjs' will-attach-webview handler wires
  // the SAME hooks onto whatever partition a webview declares, so an isolated
  // panel still renders live apps. See main.cjs setupBrowserPartition().
  isolated?: boolean;
  // Stabiler Partitionsname aus der Layout-Config (`"partition": "linkedin"`).
  // Entkoppelt den Cookie-Jar von der Panel-ID: Die Layout-Automatik darf
  // Knoten-IDs neu vergeben, ohne die Anmeldung zu verlieren (Vorfall
  // 19.08.2026 — an einem Tag zweimal passiert). Für jedes Panel, dessen
  // Sitzung überleben soll, ist das der vorgesehene Weg; `isolated` allein
  // bleibt für Bestandspanels erhalten. Details: shared/browserPartition.ts
  partition?: unknown;
  /**
   * Kuerzel des Kontos, als das diese Kachel zeigen soll (Guide-Rezept
   * `kachel.konto`, vom Server in die config gelegt). Setzt es, meldet der
   * Login-Zusatz dieses Konto selbst an — auch auf Staging und Produktion,
   * denn genau dort steht die Freigabeliste dahinter.
   */
  konto?: unknown;
  /**
   * Nach einem Verstecken per `display:none` (Kachelgroesse 0) beim Wiedereinblenden
   * neu laden. Opt-in, weil ein Neuladen ungesicherte Eingaben einer Seite verwirft —
   * fuer Kacheln, die oft versteckt werden und danach leer blieben (Fremd-Coach-Spalte,
   * 03.10.2026). Siehe wiederEingeblendet.ts.
   */
  neuLadenBeimEinblenden?: boolean;
  /**
   * Adresszeile anfangs zugeklappt, Umschalten per Fenster-Ereignis
   * `browser-adresszeile` (Knopf im Kopf des Aufrufers). Ohne Angabe gilt:
   * zugeklappt genau dann, wenn das Panel in einer Buehne steht UND eine eigene
   * CUI-Seite zeigt (lib/buehnenAdresszeile.ts). Normale Arbeitsbereiche: offen.
   */
  adresszeileEinklappbar?: boolean;
  /** Rezept `inhalt.ausfuellen` einer Buehnen-Kachel (shared/vorbefuellung.ts). Ohne Angabe: keine Vorbefuellung. */
  ausfuellen?: unknown;
  /**
   * Ladefehler des Hauptrahmens an den Aufrufer melden (Fremd-Coach-Kachel: Einmal-Ticket,
   * ein reload() der alten Adresse hilft dort nicht). Rueckgabe true = der Aufrufer versucht
   * selbst neu (dann entfaellt der eigene automatische Neuversuch); die Meldung bleibt in
   * beiden Faellen sichtbar. Ohne Angabe: bisheriges Verhalten.
   */
  onLadeFehler?: (fehler: WebviewLadeFehler) => boolean;
  /** Ersetzt den Knopf „Neu laden ↻“ der Fehlermeldung (z. B. neue Adresse statt reload der alten). */
  onNeuLaden?: () => void;
  /** Ein Ladevorgang des Hauptrahmens endete erfolgreich (kein Fehlerdokument). */
  onGeladen?: () => void;
  /** Only server-resolved CUI chat windows opt in; never arbitrary browser pages. */
  cuiChatGatewayRecovery?: boolean;
  /** Leaving the resolved chat supersedes a caller-owned ticket retry. */
  onNavigationStart?: () => void;
}

const isElectron = !!window.electronAPI?.isElectron;

/**
 * Gehoert ein eingehender Steuerbefehl zu DIESEM Panel?
 *
 * WARUM NORMALISIERT (Vorfall 21.08.2026)
 * ---------------------------------------
 * Der Server adressiert Panels als `#<nodeId>` — `resolveBrowserPanelId()` in
 * server/routes/browser-console.ts setzt das `#` davor, wenn die Layout-ID
 * keines traegt. Die Mount-Quittung in App.tsx streift es wieder ab
 * (`String(msg.panelId).replace(/^#/, '')`), die Befehls-Handler hier
 * verglichen dagegen exakt.
 *
 * Folge: jedes Panel, dessen Layout-Knoten-ID kein fuehrendes `#` hat — also
 * jedes von Hand geschriebene Layout — quittierte den Mount, fuehrte aber
 * KEINEN Befehl aus. Der Server meldete daraufhin "panel WAS mounted (owner X)
 * but did not respond" und schickte die Diagnose in Richtung toter WebSocket,
 * obwohl nur zwei Zeichenketten nicht zueinander passten. Betroffen waren
 * navigate, device, execute, eval, reload, type und keypress gleichermassen —
 * die gesamte Fernsteuerung eines solchen Panels.
 *
 * Panels, deren ID zufaellig schon ein `#` trug, funktionierten die ganze Zeit.
 * Genau diese Mischung machte den Fehler so schwer zu sehen.
 *
 * Fehlt eine der beiden IDs, gehoert der Befehl NICHT hierher: ein Panel ohne
 * ID ist ueber die API ohnehin nicht adressierbar, und ein `undefined ===
 * undefined` haette den Befehl an jedes namenlose Panel ausgeliefert.
 */
export interface WebviewLadeFehler { code: number; text: string; adresse: string }
type LadeFehlerAnzeige = WebviewLadeFehler & { neuVersuch: boolean; laedt?: boolean; reloadFehler?: string };

/**
 * Electron-`did-fail-load` → anzeigbarer Fehler, oder null, wenn es keiner fuer die
 * Kachel ist: Unterrahmen (ein fehlendes Werbe-iframe macht die Seite nicht weiss)
 * und ERR_ABORTED (-3, eine Navigation wurde von einer neueren abgeloest).
 * Ticket- und Token-Parameter werden geschwaerzt, die Meldung steht im Bild.
 */
export function webviewLadeFehler(e: { errorCode?: number; errorDescription?: string; validatedURL?: string; isMainFrame?: boolean }): WebviewLadeFehler | null {
  if (e.isMainFrame === false) return null;
  const code = typeof e.errorCode === 'number' ? e.errorCode : 0;
  if (code === -3 || code === 0) return null;
  return { code, text: e.errorDescription || 'Unbekannter Ladefehler', adresse: anzeigeAdresse(String(e.validatedURL ?? '')) };
}

/**
 * Adresse fuer Meldung und Console-Puffer: nur Herkunft + Pfad. Query und Fragment
 * fallen KOMPLETT weg statt Muster zu schwaerzen — Tickets/Tokens stehen dort unter
 * beliebigen, auch kodierten Namen (#token=, %74icket=, access_token=). Anmeldedaten
 * (user:pass@) enthaelt `origin` nicht.
 */
export function anzeigeAdresse(roh: string): string {
  if (!roh) return '(unbekannte Adresse)';
  const mitParametern = /[?#]/.test(roh);
  let u: URL;
  // fallback-ok: keine Ersatzadresse — eine nicht lesbare Adresse wird bewusst gar nicht gezeigt (koennte Geheimnisse tragen).
  try { u = new URL(roh); } catch { return '(Adresse nicht lesbar, ausgeblendet)'; }
  const basis = u.origin !== 'null' ? `${u.origin}${u.pathname}` : u.protocol;
  return mitParametern ? `${basis} (Parameter ausgeblendet)` : basis;
}

/** Safe GET destination resolved by the CUI, including the destination of a login hop. */
export function istCuiChatLadeziel(current: string, initial: string): boolean {
  try {
    const start = new URL(initial);
    const target = start.pathname === '/api/fern-guide/anmelden'
      ? new URL(start.searchParams.get('ziel') || '', start.origin) : start;
    const now = new URL(current);
    if (target.origin !== start.origin || now.origin !== target.origin || now.pathname !== '/' ||
        target.pathname !== '/' || now.username || now.password || target.username || target.password) return false;
    for (const u of [target, now]) {
      if (!['berater-chat', 'coach-chat'].includes(u.searchParams.get('panel') || '')) return false;
      if ([...u.searchParams.keys()].some(k => !['panel', 'buehne', 'sitzung'].includes(k))) return false;
      u.hash = '';
      u.searchParams.sort();
    }
    return now.href === target.href;
  } catch { return false; }
}

function isForThisPanel(incoming: unknown, own: string | undefined): boolean {
  if (typeof incoming !== 'string' || typeof own !== 'string') return false;
  return incoming.replace(/^#/, '') === own.replace(/^#/, '');
}


// Browser-panel webviews otherwise advertise the Electron UA
// (".. CUI Partner/x Chrome/146.0.7680.216 Electron/41 .."). Sites sniff that and Cloudflare
// Turnstile sent the webview into an endless challenge (03.09.2026, openalex.org). The panel
// therefore presents itself exactly like a current Chrome: no app/Electron token, frozen
// version "Chrome/<major>.0.0.0" (UA reduction). MUST stay identical to chromeKennung() in
// electron/browser-kennung.cjs. Desktop apps with that module set it process-wide
// (app.userAgentFallback), so navigator.userAgent is already clean and this attribute falls
// away; older apps only get the main document fixed — their captcha iframes still report
// Electron until the app is updated (06.10.2026, recheis.com).
const SANITIZED_UA: string | undefined = (() => {
  try {
    const m = /^(Mozilla\/5\.0 \([^)]*\)) AppleWebKit\/537\.36 \(KHTML, like Gecko\) .*?\bChrome\/(\d+)\.[\d.]+ .*?\bSafari\/537\.36$/.exec(navigator.userAgent);
    if (!m) return undefined;
    const ua = `${m[1]} AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${m[2]}.0.0.0 Safari/537.36`;
    return ua !== navigator.userAgent ? ua : undefined;
  } catch { return undefined; }
})();

// Google verweigert die Anmeldung in eingebetteten Chromium-Browsern ("Dieser Browser
// oder diese App ist unter Umstaenden nicht sicher", gemessen 03.10.2026 an
// ads.google.com im Buehnen-Fenster). Die bereinigte Chrome-Kennung reicht nicht.
// Fuer Google-Seiten gibt sich das Fenster deshalb als Firefox aus — derselbe Weg wie
// bei Ferdium/Rambox. Nicht garantiert: Google kann die Pruefung jederzeit verschaerfen.
// Rafael 03.10.2026, Buehne b-google-anmeldung-im-cui-moeglich-20261003, Antwort c.
const GOOGLE_LOGIN_UA = 'Mozilla/5.0 (Macintosh; Intel Mac OS X 14.5; rv:131.0) Gecko/20100101 Firefox/131.0';
function istGoogleSeite(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname;
    return host === 'google.com' || host.endsWith('.google.com');
  } catch { return false; }
}

// Device-viewport presets for the mobile-preview toggle. In Electron the webview
// is remounted with a matching mobile UA so responsive breakpoints AND UA-sniffing
// resolve to real mobile; the frame is sized to the device's logical (CSS) pixels
// and scaled to fit the panel. `desktop` is the default full-width behaviour.
// Logical sizes: iPhone 15 = 393x852, iPad Air (portrait) = 820x1180.
export type DeviceMode = 'desktop' | 'iphone' | 'iphone-landscape' | 'ipad';
const IOS_UA = 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_5 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.5 Mobile/15E148 Safari/604.1';
const DEVICES: Record<DeviceMode, { label: string; icon: string; w: number; h: number; ua?: string }> = {
  desktop: { label: 'Desktop', icon: '\u{1F5A5}', w: 0, h: 0 },
  iphone: { label: 'iPhone 15', icon: '\u{1F4F1}', w: 393, h: 852, ua: IOS_UA },
  'iphone-landscape': { label: 'iPhone quer', icon: '▬', w: 852, h: 393, ua: IOS_UA },
  ipad: {
    label: 'iPad', icon: '▭', w: 820, h: 1180,
    ua: 'Mozilla/5.0 (iPad; CPU OS 17_5 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.5 Mobile/15E148 Safari/604.1',
  },
};

function normaliseDevice(value: unknown): DeviceMode {
  return value === 'iphone' || value === 'iphone-landscape' || value === 'ipad' ? value : 'desktop';
}

export default function BrowserPanel({ initialUrl = '', panelId, onUrlChange, isolated = false, partition: partitionName, konto, neuLadenBeimEinblenden = false, adresszeileEinklappbar, ausfuellen, onLadeFehler, onNeuLaden, onGeladen, onNavigationStart, cuiChatGatewayRecovery = false }: BrowserPanelProps) {
  // Rueckrufe per Ref: neue Funktionsidentitaeten duerfen die Webview-Listener nicht neu anhaengen (das bricht laufende Neuversuche ab).
  const rueckrufeRef = useRef({ onLadeFehler, onNeuLaden, onGeladen, onNavigationStart });
  rueckrufeRef.current = { onLadeFehler, onNeuLaden, onGeladen, onNavigationStart };
  // Session-Partition des <webview>. Auflösung + Begründung: shared/browserPartition.ts.
  // Wirft bei unbrauchbarer Konfiguration statt still auf die geteilte Partition
  // zurückzufallen — der stille Rückfall meldet sonst alle Panels an derselben
  // Sitzung an. Der Wurf landet in der ErrorBoundary um dieses Panel.
  const { user, authEnabled } = useAuth();
  const partition = resolveBrowserPartition({ partitionName, isolated, panelId });
  /*
   * WESSEN SITZUNG ZEIGT DIESE KACHEL?
   *
   * Kommt aus dem Guide-Rezept (`kachel.konto`) und ist der Grund, warum die
   * Kachel eine eigene Cookie-Box hat. Ausserhalb von Electron gibt es diese
   * Box nicht: ein <iframe> teilt die Cookies des CUI-Fensters. Frueher wurde
   * `partition` dort STILL ignoriert — die Kachel sah aus wie eine eigene
   * Anmeldung und war die geteilte. Mit einem Konto ist das nicht mehr nur
   * unsauber, sondern irrefuehrend: Rafael saehe eine Sitzung und hielte sie
   * fuer die des genannten Nutzers. Deshalb sagt die Kachel es hier laut,
   * statt so zu tun als ob.
   *
   * Bis 04.10.2026 zeigte sie dann GAR NICHTS. Rafael (b-mo-energy-einbetten):
   * "Das muss jeder Partner genau so koennen wie ich" — Partner sitzen im
   * Browser, nicht in der CUI-App. Seither laedt die Kachel die Seite im
   * <iframe> OHNE automatische Anmeldung, und ein Streifen ueber der Seite
   * sagt, dass hier die eigene Anmeldung dieses Fensters gilt, nicht die
   * von `konto`. Der Hinweis ersetzt die Sperre; still wird es nicht.
   */
  const kontoName = typeof konto === 'string' && konto.trim() ? konto.trim() : null;
  const kontoOhneBox = !!kontoName && !isElectron;
  // Persist URL per panel instance in localStorage
  const storageKey = panelId ? `browser-url-${panelId}` : '';
  // initialUrl from layout config takes priority; localStorage only used if no config URL set
  const storedUrl = storageKey ? localStorage.getItem(storageKey) : null;
  const restoredUrl = initialUrl || storedUrl || '';
  const [url, setUrl] = useState(restoredUrl);
  const [inputValue, setInputValue] = useState(restoredUrl);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  // Incrementing key destroys + recreates iframe/webview = guaranteed fresh load
  const [reloadKey, setReloadKey] = useState(0);
  // Device-viewport preview mode, persisted per panel (like the URL).
  const deviceKey = panelId ? `browser-device-${panelId}` : '';
  const [deviceMode, setDeviceMode] = useState<DeviceMode>(() =>
    normaliseDevice(deviceKey ? localStorage.getItem(deviceKey) : null),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  // Buehnen-Kacheln halten fest, was darin abgeschickt wurde — abrufbar nach dem Aushaengen (shared/kachel-protokoll.ts).
  useKachelProtokoll(panelId, isElectron, containerRef);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentSize, setContentSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const webviewRef = useRef<HTMLWebViewElement | null>(null);
  // Sites that send X-Frame-Options / CSP frame-ancestors refuse to render in the
  // <iframe> path (non-Electron). The browser leaves the frame at about:blank and
  // reports nothing — the panel just stayed white, which reads as "broken" to a
  // non-technical user. Probe after load: a page that actually loaded is
  // cross-origin, so touching contentWindow.location.href THROWS. A refused one
  // is still same-origin about:blank and reads back fine → that's the tell.
  const [embedBlocked, setEmbedBlocked] = useState(false);
  useEffect(() => { setEmbedBlocked(false); }, [url, reloadKey]);
  /*
   * LADEFEHLER DER WEBVIEW SICHTBAR MACHEN (Rafael, 17.10.2026, zweimal an einem Tag:
   * „der vize chat kann wieder nicht geladen werden"). Scheitert in Electron die
   * Navigation selbst (Netz, DNS, Zertifikat), steht die <webview> auf
   * chrome-error://chromewebdata/ mit leerem Dokument — auf dem weissen Grund unten
   * eine komplett weisse Flaeche ohne jeden Grund. Gemessen an der Fremd-Coach-Kachel:
   * history.length 1 (schon der erste Aufruf scheiterte), ein Neuladen zeigte sofort
   * den Chat. Deshalb: einmal selbst neu versuchen, danach sichtbar mit Grund stehen
   * bleiben, und den Fehler in den Console-Puffer schreiben (/api/browser-console).
   * Die Meldung verschwindet erst, wenn ein Ladevorgang des Hauptrahmens ERFOLGREICH
   * endet — weder Ladestart, dom-ready noch ein Adresswechsel beweisen das. Erfolg heisst:
   * did-finish-load, ohne dass seit dem letzten echten Commit (did-navigate) ein
   * did-fail-load kam. Electron feuert auch fuer das Fehlerdokument did-finish-load, und
   * getURL() liefert dabei die urspruengliche Adresse statt chrome-error:// (26.10.2026,
   * ERR_NAME_NOT_RESOLVED an der Fremd-Coach-Kachel: Meldung weg, Kachel weiss). did-navigate
   * kommt nur fuer Commits ohne Fehlerseite (Electron DidFinishNavigation). Der Neuversuch gehoert zu genau der Webview-Instanz, die
   * scheiterte: jeder neue Ladestart, URL-/Instanzwechsel oder Unmount hebt ihn auf.
   * Wirft reload(), wird NICHT neu aufgebaut (das war ein moeglicher Kreislauf), der
   * Grund steht in der Meldung.
   */
  const [ladeFehler, setLadeFehler] = useState<LadeFehlerAnzeige | null>(null);
  // Fehlerzustand des laufenden Ladevorgangs: gesetzt in did-fail-load, geloescht nur durch did-navigate (echter Commit).
  const ladevorgangGescheitertRef = useRef(false);
  const autoNeuVersuchRef = useRef(0);
  const gatewayVersucheRef = useRef(0);
  const gatewayZielRef = useRef<string | null>(null);
  const neuVersuchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const neuVersuchAbbrechen = useCallback(() => {
    if (neuVersuchTimerRef.current !== null) { clearTimeout(neuVersuchTimerRef.current); neuVersuchTimerRef.current = null; }
  }, []);
  /*
   * NACHLADEN NACH VORUEBERGEHENDEM NETZFEHLER (01.11.2026, 13:20Z: DNS auf Rafaels Mac eine halbe
   * Minute weg, Konto-Kacheln blieben auf -105 stehen). Netzfehler des Hauptrahmens werden mit Backoff
   * 2/5/10/30/60 s, dann alle 60 s nachgeladen, solange die Kachel eingehaengt ist; sofort (entprellt)
   * bei window 'online' und beim Sichtbarwerden des Fensters. Logik: lib/webviewNachladen.ts.
   * Nicht in Aufnahme/Shadow-Client: dort bleibt es beim einen Neuversuch, damit kein Nachladen
   * mitten in eine Aufnahme faellt. Nicht bei Aufrufern mit eigenem onLadeFehler (Einmal-Ticket).
   */
  const aufnahmeKontextRef = useRef(false);
  aufnahmeKontextRef.current = isShadowClient() || user?.id === 'shadow-client' || user?.id === 'aufnahme';
  const planerRef = useRef<NachladePlaner | null>(null);
  const [nachlade, setNachlade] = useState<NachladeZustand | null>(null);
  const [uhrTick, setUhrTick] = useState(() => Date.now());
  // Adress-/Instanzwechsel ist kein Erfolg: der bisherige Grund bleibt stehen, nur als „laedt".
  useEffect(() => {
    autoNeuVersuchRef.current = 0;
    gatewayVersucheRef.current = 0;
    gatewayZielRef.current = null;
    planerRef.current?.zuruecksetzen();
    setLadeFehler((alt) => (alt ? { ...alt, neuVersuch: false, laedt: true } : alt));
  }, [url, reloadKey, deviceMode]);
  const nachLadefehlerNeuLaden = useCallback((wv: any) => {
    neuVersuchAbbrechen();
    if (!wv || wv !== webviewRef.current) return; // Instanz inzwischen ersetzt — deren Fehler zaehlen selbst
    // fallback-ok: kein Ersatzwert und kein Neuaufbau — scheitert reload(), steht der Grund sichtbar in der Meldung und in der Console.
    try {
      if (gatewayZielRef.current) {
        // Explicit GET: reload() could resubmit the document's original POST.
        if (gatewayVersucheRef.current >= 5) {
          planerRef.current?.zuruecksetzen();
          setLadeFehler(alt => alt ? { ...alt, neuVersuch: false, laedt: false } : alt);
          return;
        }
        gatewayVersucheRef.current += 1;
        void wv.loadURL(gatewayZielRef.current).catch((e: unknown) => {
          // did-fail-load owns recovery; retain a visible diagnostic if loadURL rejects.
          console.warn('[BrowserPanel] CUI gateway GET failed:', e);
        });
      } else wv.reload();
    } catch (e: any) {
      const grund = String(e?.message ?? e);
      console.warn(`[BrowserPanel${panelId ? ` ${panelId}` : ''}] reload nach Ladefehler gescheitert:`, e);
      setLadeFehler((alt) => (alt ? { ...alt, neuVersuch: false, laedt: false, reloadFehler: grund } : alt));
    }
  }, [panelId, neuVersuchAbbrechen]);
  const nachladenRef = useRef(nachLadefehlerNeuLaden);
  nachladenRef.current = nachLadefehlerNeuLaden;
  useEffect(() => {
    if (!isElectron) return;
    const planer = erzeugeNachladePlaner({
      uhr: { jetzt: () => Date.now(), setTimeout: (fn, ms) => window.setTimeout(fn, ms), clearTimeout: (h) => window.clearTimeout(h as number) },
      nachladen: () => nachladenRef.current(webviewRef.current),
      anzeigen: setNachlade,
      istVerborgen: () => document.visibilityState === 'hidden',
    });
    planerRef.current = planer;
    const beiOnline = () => { planer.sofort('online'); };
    const beiSichtbar = () => { if (document.visibilityState === 'visible') planer.sofort('sichtbar'); };
    window.addEventListener('online', beiOnline);
    document.addEventListener('visibilitychange', beiSichtbar);
    return () => {
      window.removeEventListener('online', beiOnline);
      document.removeEventListener('visibilitychange', beiSichtbar);
      planer.abbauen();
      planerRef.current = null;
    };
  }, []);
  // Sekundentakt nur fuer die Anzeige „naechster Versuch in N s", nur solange ein Versuch geplant ist.
  const naechsterVersuchUm = nachlade?.naechsterVersuchUm ?? null;
  useEffect(() => {
    if (naechsterVersuchUm === null) return;
    setUhrTick(Date.now());
    const t = window.setInterval(() => setUhrTick(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [naechsterVersuchUm]);
  const handleIframeLoad = useCallback(() => {
    if (!url) return;
    try {
      const href = iframeRef.current?.contentWindow?.location?.href;
      setEmbedBlocked(href === 'about:blank');
    } catch {
      setEmbedBlocked(false); // cross-origin access threw = the page really loaded
    }
  }, [url]);
  // Oeffnet die AKTUELLE Adresse, nicht die Startadresse. In Electron zieht
  // `url` Navigationen im <webview> bewusst nicht nach (siehe onNavigate), die
  // aktuelle Adresse liefert dort nur getURL(). Im <iframe> ist `url` die
  // aktuellste Adresse, die wir kennen: Navigationen innerhalb eines fremden
  // Rahmens sind fuer uns nicht lesbar (Same-Origin-Policy).
  const openInNewTab = useCallback(() => {
    const wv = webviewRef.current as { getURL?: () => string } | null;
    const ziel = (isElectron && wv?.getURL?.()) || url;
    if (ziel) window.open(ziel, '_blank', 'noopener,noreferrer');
  }, [url]);
  /*
   * „IM EIGENEN TAB OEFFNEN" IMMER SICHTBAR (Rafael, 04.10.2026). Ausserhalb von
   * Electron ist jedes Fenster ein <iframe>. iPad-Safari (ITP) verwirft Cookies
   * in seitenfremden Rahmen: die Seite laedt, aber die Anmeldung haelt nicht —
   * ohne diesen Knopf gab es dann keinen Ausweg (das Overlay unten erscheint nur,
   * wenn die Seite das Einbetten ganz verweigert). Nur fuer absolute http(s)-
   * Adressen FREMDER Herkunft: eine Seite dieses CUI (relativ, z. B.
   * /api/guide/buehnen/.../seite.html) ist im Rahmen erstanbieterig, ihre
   * Cookies gelten — dort reicht das kleine ↗. In Electron unveraendert: die
   * <webview> hat ihren eigenen Cookie-Topf, das Problem tritt dort nicht auf.
   */
  const zeigeTabKnopf = !isElectron && istFremdeHttpAdresse(url);

  /*
   * Eine Browser-Kachel auf der Buehne ist ein Browser, keine Vorschau: ihre
   * kompakte Adresszeile mit Zurueck/Vor/Neu laden bleibt deshalb immer sichtbar.
   * Ein ausdruecklicher Einklapp-Wunsch gilt nur ausserhalb einer Buehne.
   */
  const inBuehne = useContext(BuehnenKontext);
  const buehnenKennung = useContext(BuehnenKennungKontext);
  const einklappbar = !inBuehne && (adresszeileEinklappbar ?? false);
  const [adresszeileOffen, setAdresszeileOffen] = useState(false);
  const adresszeileZu = einklappbar && !adresszeileOffen;
  /* Platz in der Adresszeile fuer den eingeklappten Login-Knopf (AppLoginPanel, per Portal). */
  const [loginPlatz, setLoginPlatz] = useState<HTMLSpanElement | null>(null);
  useEffect(() => {
    const umschalten = (e: Event) => {
      const d = (e as CustomEvent).detail as { panelId?: string; offen?: boolean };
      if (!isForThisPanel(d?.panelId, panelId)) return;
      setAdresszeileOffen(v => (typeof d.offen === 'boolean' ? d.offen : !v));
    };
    const oeffnen = (e: Event) => {
      const d = (e as CustomEvent).detail as { panelId?: string };
      if (isForThisPanel(d?.panelId, panelId)) openInNewTab();
    };
    window.addEventListener(ADRESSZEILE_EREIGNIS, umschalten);
    window.addEventListener(OEFFNEN_EREIGNIS, oeffnen);
    return () => { window.removeEventListener(ADRESSZEILE_EREIGNIS, umschalten); window.removeEventListener(OEFFNEN_EREIGNIS, oeffnen); };
  }, [panelId, openInNewTab]);

  const applyDevice = useCallback((mode: DeviceMode) => {
    setDeviceMode(mode);
    if (deviceKey) localStorage.setItem(deviceKey, mode);
  }, [deviceKey]);
  // Buffer of console-message events from the webview, flushed to the server.
  const consoleBufferRef = useRef<Array<{ level: string; message: string; line?: number; source?: string; at: string }>>([]);

  // Listen for global nuclear-refresh event (from toolbar button)
  useEffect(() => {
    const handler = () => hardRefresh();
    window.addEventListener('nuclear-refresh', handler);
    return () => window.removeEventListener('nuclear-refresh', handler);
  }, []);

  // Signal to the server that this browser panel is mounted and ready for
  // headless commands (eval/click/type). Used by ensurePanelMounted() to
  // resolve the mount-wait Promise before dispatching the first command.
  //
  // clientId comes from the WS client:hello message (stashed on window by
  // App.tsx). Including it lets the server record us as the panel owner so
  // follow-up eval/click/screenshot calls route directly to this client via
  // sendToClient() instead of broadcasting — eliminating the "non-owner
  // client races to 'Panel not found' and wins" failure mode.
  //
  // The local registry is the other half: the server never hears when this panel
  // unmounts, so App.tsx answers commands for an unmounted panel itself
  // (lib/gemounteteBrowserPanels.ts). Registered before /ready goes out.
  useEffect(() => {
    if (!panelId) return;
    const abmelden = meldeBrowserPanelAn(panelId);
    const clientId = (window as unknown as { __CUI_CLIENT_ID__?: string }).__CUI_CLIENT_ID__;
    void meldeBrowserPanelBereit(panelId, clientId).catch(() => {});
    return () => {
      // Die Kennung wechselt bei jeder WebSocket-Neuverbindung. Nicht die beim
      // Mount eingefrorene (unter Umstaenden noch fehlende) Kennung abmelden,
      // sondern den Besitzer der aktuell laufenden Verbindung.
      const aktuellerClientId = (window as unknown as { __CUI_CLIENT_ID__?: string }).__CUI_CLIENT_ID__;
      if (!abmelden() || !aktuellerClientId) return;
      void meldeBrowserPanelFort(panelId, aktuellerClientId)
        .catch(err => console.error('[BrowserPanel] /gone fehlgeschlagen', panelId, err));
    };
  }, [panelId]);

  // External navigation: control:browser-navigate (relayed by App.tsx as a
  // window event). Lets the panel be driven headlessly via the API.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { panelId?: string; url?: string };
      if (!detail?.url || !isForThisPanel(detail.panelId, panelId)) return;
      setUrl(detail.url);
      setInputValue(detail.url);
      if (!(inBuehne && kontoName && appPortZwischenseite(detail.url, initialUrl))) {
        if (storageKey) localStorage.setItem(storageKey, detail.url);
        onUrlChange?.(detail.url);
      }
    };
    window.addEventListener('browser-navigate', handler);
    return () => window.removeEventListener('browser-navigate', handler);
  }, [panelId, storageKey, inBuehne, kontoName, initialUrl]);

  // External device-viewport switch: control:browser-device (relayed by App.tsx).
  // Lets a headless caller flip the panel into iPhone/iPad preview.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { panelId?: string; device?: string };
      if (!isForThisPanel(detail.panelId, panelId)) return;
      const mode = normaliseDevice(detail.device);
      setDeviceMode(mode);
      if (deviceKey) localStorage.setItem(deviceKey, mode);
    };
    window.addEventListener('browser-device', handler);
    return () => window.removeEventListener('browser-device', handler);
  }, [panelId, deviceKey]);

  // External JS execution: control:browser-execute. Wraps the user script so
  // its return value (or thrown error) lands in the page's console output,
  // tagged with the requestId — pollable via /api/browser-console/<panelId>.
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail as { panelId?: string; requestId?: string; script?: string };
      if (!detail?.script || !isForThisPanel(detail.panelId, panelId)) return;
      const wv = webviewRef.current as any;
      if (!wv?.executeJavaScript) return;
      const tag = `[EXEC:${detail.requestId || 'noid'}]`;
      const wrapped =
        '(async () => { try {' +
        ` const __v = await (async () => { ${detail.script} })();` +
        ` console.log(${JSON.stringify(tag)}, typeof __v === 'string' ? __v : JSON.stringify(__v));` +
        ' } catch (e) {' +
        ` console.error(${JSON.stringify(tag + ' ERROR')}, (e && e.message) || String(e));` +
        ' } })()';
      try { await wv.executeJavaScript(wrapped); }
      catch (err) { console.error('[BrowserPanel] executeJavaScript failed', err); }
    };
    window.addEventListener('browser-execute', handler);
    return () => window.removeEventListener('browser-execute', handler);
  }, [panelId]);

  // Synchronous JS eval: control:browser-eval. The script runs inside the
  // webview, and the return value (JSON-serialisable) is POSTed back to
  // /api/browser-panel/eval-result keyed by correlationId. The server's /eval
  // endpoint is waiting on a Promise that this resolves.
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail as { panelId?: string; correlationId?: string; script?: string };
      if (!detail?.script || !detail.correlationId || !isForThisPanel(detail.panelId, panelId)) return;
      const wv = webviewRef.current as any;
      const correlationId = detail.correlationId;
      let body: { correlationId: string; result?: unknown; error?: string };
      if (!wv?.executeJavaScript) {
        body = { correlationId, error: 'webview not ready (panel may be using iframe fallback)' };
      } else {
        const wrapped =
          '(async () => { try {' +
          ` const __v = await (async () => { ${detail.script} })();` +
          ' return { ok: true, value: __v };' +
          ' } catch (e) {' +
          ' return { ok: false, error: (e && e.message) || String(e) };' +
          ' } })()';
        try {
          const outcome = await wv.executeJavaScript(wrapped);
          if (outcome && outcome.ok) {
            body = { correlationId, result: outcome.value };
          } else {
            body = { correlationId, error: (outcome && outcome.error) || 'unknown eval failure' };
          }
        } catch (err) {
          body = { correlationId, error: (err as Error)?.message || String(err) };
        }
      }
      try {
        await fetch('/api/browser-panel/eval-result', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } catch (err) {
        console.error('[BrowserPanel] failed to POST eval-result', err);
      }
    };
    window.addEventListener('browser-eval', handler);
    return () => window.removeEventListener('browser-eval', handler);
  }, [panelId]);

  // Reload the embedded page. clearCache uses reloadIgnoringCache() so a stale
  // JS bundle (after a Next.js rebuild) actually re-downloads. For the iframe
  // fallback we bump reloadKey to remount.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { panelId?: string; clearCache?: boolean };
      if (!isForThisPanel(detail?.panelId, panelId)) return;
      const wv = webviewRef.current as any;
      if (wv && detail.clearCache && typeof wv.reloadIgnoringCache === 'function') {
        wv.reloadIgnoringCache();
      } else if (wv && typeof wv.reload === 'function') {
        wv.reload();
      } else {
        // Iframe fallback: remount the iframe to force a full reload.
        setReloadKey((k) => k + 1);
      }
    };
    window.addEventListener('browser-reload', handler);
    return () => window.removeEventListener('browser-reload', handler);
  }, [panelId]);

  // Type a string into the currently focused element via webview.sendInputEvent.
  // Acks via /api/browser-panel/eval-result so the server's /type can resolve.
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        panelId?: string; correlationId?: string; text?: string; delayMs?: number;
      };
      if (!detail?.correlationId || !isForThisPanel(detail.panelId, panelId)) return;
      const wv = webviewRef.current as any;
      const correlationId = detail.correlationId;
      let body: { correlationId: string; result?: unknown; error?: string };
      if (!wv?.sendInputEvent) {
        body = { correlationId, error: 'webview.sendInputEvent unavailable (iframe fallback?)' };
      } else {
        try {
          const text = String(detail.text ?? '');
          const delayMs = Math.min(200, Math.max(0, detail.delayMs ?? 0));
          for (const ch of text) {
            wv.sendInputEvent({ type: 'char', keyCode: ch });
            if (delayMs > 0) await new Promise((r) => setTimeout(r, delayMs));
          }
          body = { correlationId, result: { typed: text.length } };
        } catch (err) {
          body = { correlationId, error: (err as Error)?.message || String(err) };
        }
      }
      try {
        await fetch('/api/browser-panel/eval-result', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } catch (err) {
        console.error('[BrowserPanel] failed to POST type ack', err);
      }
    };
    window.addEventListener('browser-type', handler);
    return () => window.removeEventListener('browser-type', handler);
  }, [panelId]);

  // Send a single named key (with optional modifiers) via sendInputEvent. The
  // key string follows Electron's conventions: "Enter", "Tab", "Escape",
  // "ArrowDown", "Backspace", or a literal character like "s" (with the
  // "control" modifier for Ctrl+S).
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        panelId?: string; correlationId?: string; key?: string; modifiers?: string[];
      };
      if (!detail?.correlationId || !isForThisPanel(detail.panelId, panelId)) return;
      const wv = webviewRef.current as any;
      const correlationId = detail.correlationId;
      let body: { correlationId: string; result?: unknown; error?: string };
      if (!wv?.sendInputEvent) {
        body = { correlationId, error: 'webview.sendInputEvent unavailable (iframe fallback?)' };
      } else {
        try {
          const key = String(detail.key ?? '');
          const modifiers = Array.isArray(detail.modifiers) ? detail.modifiers : [];
          wv.sendInputEvent({ type: 'keyDown', keyCode: key, modifiers });
          // For printable single chars, also fire the 'char' event so the page
          // actually receives input — keyDown alone does not produce text.
          if (key.length === 1 && !modifiers.some((m) => ['control', 'meta'].includes(m))) {
            wv.sendInputEvent({ type: 'char', keyCode: key, modifiers });
          }
          wv.sendInputEvent({ type: 'keyUp', keyCode: key, modifiers });
          body = { correlationId, result: { key, modifiers } };
        } catch (err) {
          body = { correlationId, error: (err as Error)?.message || String(err) };
        }
      }
      try {
        await fetch('/api/browser-panel/eval-result', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } catch (err) {
        console.error('[BrowserPanel] failed to POST keypress ack', err);
      }
    };
    window.addEventListener('browser-keypress', handler);
    return () => window.removeEventListener('browser-keypress', handler);
  }, [panelId]);

  function navigate() {
    let target = inputValue.trim();
    if (!target) return;
    // Auto-correct localhost/127.0.0.1 → server-side proxy.
    // The browser panel runs in the user's browser (e.g. Mac); 'localhost' there
    // points to the user's machine, not the server. devPortUrl rewrites it to
    // a reachable origin (subdomain proxy on partner, direct host:port on dev).
    const lhMatch = target.match(/^(?:https?:\/\/)?(?:localhost|127\.0\.0\.1)(?::(\d+))?(\/.*)?$/i);
    if (lhMatch) {
      const port = parseInt(lhMatch[1] || '80', 10);
      const path = lhMatch[2] || '/';
      target = devPortUrl(port, path);
    } else if (!target.startsWith('http') && !target.startsWith('/')) {
      if (!target.includes('.') || target.includes(' ')) {
        target = `https://www.google.com/search?q=${encodeURIComponent(target)}`;
      } else {
        target = 'https://' + target;
      }
    }
    setUrl(target);
    setInputValue(target);
    if (!(inBuehne && kontoName && appPortZwischenseite(target, initialUrl))) {
      if (storageKey) localStorage.setItem(storageKey, target);
      onUrlChange?.(target);
    }
  }

  // Webview event listeners (Electron only)
  useEffect(() => {
    if (!isElectron || !url) return;
    const wv = webviewRef.current as any;
    if (!wv) return;

    const onNavigate = (e?: { httpResponseCode?: number }) => {
      ladevorgangGescheitertRef.current = false; // echter Commit: der neue Ladevorgang hat (noch) keinen Fehler
      const currentUrl = wv.getURL?.() ?? url;
      setInputValue(currentUrl);
      // `url` darf hier NICHT nachgezogen werden: es haengt als src={url} am
      // <webview>, und ein src-Attribut-Wechsel loest in Electron eine NEUE
      // Navigation aus. Bei Seiten, die pro Load eine andere URL erzeugen
      // (Google-Ads-Signup rotiert Tokens in den Query-Params), entsteht sonst
      // ein Reload-Loop im Sekundentakt. `url` wechselt nur bei expliziter
      // Navigation (Adressleiste, browser-navigate); Anzeige/Restore laufen
      // ueber inputValue + localStorage.
      if (!(inBuehne && kontoName && appPortZwischenseite(currentUrl, initialUrl))) {
        if (storageKey) localStorage.setItem(storageKey, currentUrl);
        onUrlChange?.(currentUrl);
      }
      setCanGoBack(wv.canGoBack?.() ?? false);
      setCanGoForward(wv.canGoForward?.() ?? false);
      const code = e?.httpResponseCode ?? 0;
      if (!cuiChatGatewayRecovery || code < 400) {
        gatewayZielRef.current = null;
        return;
      }
      // HTTP error HTML also emits did-finish-load. It must not confirm a healthy chat.
      ladevorgangGescheitertRef.current = true;
      neuVersuchAbbrechen();
      const fehler = { code, text: `HTTP ${code}`, adresse: anzeigeAdresse(currentUrl) };
      consoleBufferRef.current.push({ level: 'error', message: `[BrowserPanel] ${fehler.text} — ${fehler.adresse}`, at: new Date().toISOString() });
      const voruebergehend = [502, 503, 504].includes(code);
      let neuVersuch = false;
      gatewayZielRef.current = null;
      if (voruebergehend && !aufnahmeKontextRef.current &&
          istCuiChatLadeziel(currentUrl, initialUrl)) {
        gatewayZielRef.current = currentUrl;
        neuVersuch = gatewayVersucheRef.current < 5;
        if (neuVersuch) planerRef.current?.fehler(code);
      } else if (voruebergehend && currentUrl === initialUrl && rueckrufeRef.current.onLadeFehler) {
        // Ticket owner requests a NEW ticket; never replay the login hop.
        try { neuVersuch = rueckrufeRef.current.onLadeFehler(fehler) === true; }
        catch (err) { console.error('[BrowserPanel] onLadeFehler warf:', err); }
      }
      if (!neuVersuch) planerRef.current?.zuruecksetzen();
      setLadeFehler({ ...fehler, neuVersuch });
    };

    // Console output from the embedded page. Electron's <webview> emits
    // `console-message` with a numeric level (0=log, 1=info, 2=warning, 3=error).
    const LEVEL_NAMES = ['log', 'info', 'warning', 'error'];
    const onConsoleMessage = (e: any) => {
      consoleBufferRef.current.push({
        level: typeof e.level === 'number' ? (LEVEL_NAMES[e.level] ?? 'log') : String(e.level ?? 'log'),
        message: String(e.message ?? ''),
        line: typeof e.line === 'number' ? e.line : undefined,
        source: e.sourceId || undefined,
        at: new Date().toISOString(),
      });
    };

    // Ladestart: ausstehender Neuversuch ist ueberholt; die Meldung bleibt, nur als „laedt".
    const onStartLoading = () => {
      neuVersuchAbbrechen();
      planerRef.current?.ladeStart();
      setLadeFehler((alt) => (alt ? { ...alt, laedt: true } : alt));
    };
    const onErfolg = () => {
      const jetzt = String(wv.getURL?.() ?? '');
      // Fehlerdokument ist kein Erfolg — erkennbar am Fehler dieses Ladevorgangs, nicht verlaesslich an der Adresse.
      if (ladevorgangGescheitertRef.current || jetzt.startsWith('chrome-error:')) return;
      neuVersuchAbbrechen();
      autoNeuVersuchRef.current = 0;
      gatewayVersucheRef.current = 0;
      gatewayZielRef.current = null;
      planerRef.current?.erfolg();
      setLadeFehler(null);
      rueckrufeRef.current.onGeladen?.();
    };
    const onFailLoad = (e: any) => {
      const fehler = webviewLadeFehler(e);
      if (!fehler) return;
      consoleBufferRef.current.push({
        level: 'error',
        message: `[BrowserPanel] Laden gescheitert: ${fehler.text} (${fehler.code}) — ${fehler.adresse}`,
        at: new Date().toISOString(),
      });
      ladevorgangGescheitertRef.current = true;
      neuVersuchAbbrechen();
      if (cuiChatGatewayRecovery && e.validatedURL !== initialUrl &&
          !istCuiChatLadeziel(String(e.validatedURL ?? ''), initialUrl)) {
        planerRef.current?.zuruecksetzen();
        gatewayZielRef.current = null;
        setLadeFehler({ ...fehler, neuVersuch: false });
        return;
      }
      const meldeAn = rueckrufeRef.current.onLadeFehler;
      if (meldeAn) {
        // Aufrufer entscheidet ueber den Neuversuch (neue Adresse); die Meldung steht so oder so.
        let aufruferVersuchtNeu = false;
        try { aufruferVersuchtNeu = meldeAn(fehler) === true; } catch (err) {
          console.error(`[BrowserPanel${panelId ? ` ${panelId}` : ''}] onLadeFehler warf:`, err);
        }
        setLadeFehler({ ...fehler, neuVersuch: aufruferVersuchtNeu });
        return;
      }
      const planer = planerRef.current;
      if (planer && !aufnahmeKontextRef.current && istVoruebergehenderLadefehler(e)) {
        planer.fehler(fehler.code);
        setLadeFehler({ ...fehler, neuVersuch: true });
        return;
      }
      planer?.zuruecksetzen(); // kein Netzfehler (z. B. Zertifikat): kein Dauernachladen, alter Einzelversuch
      const neuVersuch = autoNeuVersuchRef.current < 1;
      setLadeFehler({ ...fehler, neuVersuch });
      if (neuVersuch) {
        autoNeuVersuchRef.current += 1;
        neuVersuchTimerRef.current = setTimeout(() => nachLadefehlerNeuLaden(wv), 2000);
      }
    };

    const onNavigationStart = (e: { isMainFrame?: boolean; url?: string; isInPlace?: boolean }) => {
      if (e.isMainFrame === false) return;
      // A caller-owned timer must not reopen a chat after the user leaves it.
      const erwartet = e.url === initialUrl || istCuiChatLadeziel(e.url ?? '', initialUrl);
      neuVersuchAbbrechen();
      if (cuiChatGatewayRecovery && (!erwartet || e.isInPlace)) {
        rueckrufeRef.current.onNavigationStart?.();
        gatewayZielRef.current = null;
        planerRef.current?.zuruecksetzen();
        setLadeFehler(alt => alt ? { ...alt, neuVersuch: false, laedt: false } : alt);
      } else planerRef.current?.ladeStart();
    };
    const onNavigateInPage = () => {
      const gescheitert = ladevorgangGescheitertRef.current;
      onNavigate();
      ladevorgangGescheitertRef.current = gescheitert;
      if (cuiChatGatewayRecovery) {
        rueckrufeRef.current.onNavigationStart?.();
        gatewayZielRef.current = null;
        planerRef.current?.zuruecksetzen();
        setLadeFehler(alt => alt ? { ...alt, neuVersuch: false, laedt: false } : alt);
      }
    };

    wv.addEventListener('did-start-navigation', onNavigationStart);
    wv.addEventListener('did-navigate', onNavigate);
    wv.addEventListener('did-navigate-in-page', onNavigateInPage);
    wv.addEventListener('console-message', onConsoleMessage);
    wv.addEventListener('did-start-loading', onStartLoading);
    wv.addEventListener('did-fail-load', onFailLoad);
    wv.addEventListener('did-finish-load', onErfolg);

    return () => {
      wv.removeEventListener('did-start-navigation', onNavigationStart);
      wv.removeEventListener('did-navigate', onNavigate);
      wv.removeEventListener('did-navigate-in-page', onNavigateInPage);
      wv.removeEventListener('console-message', onConsoleMessage);
      wv.removeEventListener('did-start-loading', onStartLoading);
      wv.removeEventListener('did-fail-load', onFailLoad);
      wv.removeEventListener('did-finish-load', onErfolg);
      neuVersuchAbbrechen();
    };
  }, [url, reloadKey, deviceMode, nachLadefehlerNeuLaden, neuVersuchAbbrechen, panelId, inBuehne, kontoName, initialUrl, storageKey, cuiChatGatewayRecovery]);

  // Track the content-area size so the device frame can be scaled to fit.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () => setContentSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [url, deviceMode]);

  // Wiedereinblenden nach display:none → neu laden (nur mit neuLadenBeimEinblenden).
  // Beobachtet die aeussere Huelle, weil sie bei JEDEM versteckenden Vorfahren auf 0
  // faellt, egal welche Regel es war. Electron: wv.reload() laedt die AKTUELLE Adresse
  // (nicht ein schon eingeloestes Einmal-Ticket aus src). Im iframe-Pfad nichts tun —
  // ein iframe zeichnet nach display:none korrekt neu.
  useEffect(() => {
    if (!neuLadenBeimEinblenden || !isElectron) return;
    const el = containerRef.current;
    if (!el) return;
    let vorher: Groesse | null = null;
    const pruefen = () => {
      const jetzt = { w: el.clientWidth, h: el.clientHeight };
      const wieder = wurdeWiederEingeblendet(vorher, jetzt);
      vorher = jetzt;
      if (!wieder) return;
      // Steht ein Ladefehler und hat der Aufrufer einen eigenen Neu-laden-Weg (Einmal-Ticket), dann diesen —
      // ein reload() der nie eingeloesten Ticket-Adresse liefe sonst in eine abgelaufene Anmeldung.
      const ersatz = rueckrufeRef.current.onNeuLaden;
      if (ladevorgangGescheitertRef.current && ersatz) {
        console.info(`[BrowserPanel${panelId ? ` ${panelId}` : ''}] wieder eingeblendet nach Ladefehler — Aufrufer laedt neu`);
        ersatz();
        return;
      }
      const wv = webviewRef.current as any;
      if (wv && typeof wv.reload === 'function') {
        console.info(`[BrowserPanel${panelId ? ` ${panelId}` : ''}] wieder eingeblendet — Webview wird neu geladen`);
        try { wv.reload(); } catch (e) {
          console.warn('[BrowserPanel] reload nach Einblenden gescheitert, Webview wird neu aufgebaut:', e);
          setReloadKey((k) => k + 1);
        }
      }
    };
    pruefen();
    const ro = new ResizeObserver(pruefen);
    ro.observe(el);
    return () => ro.disconnect();
  }, [neuLadenBeimEinblenden, panelId]);

  // Flush buffered console messages to the server so the history is
  // retrievable via GET /api/browser-console/:panelId.
  useEffect(() => {
    if (!isElectron || !panelId) return;
    const flush = () => {
      const batch = consoleBufferRef.current.splice(0, consoleBufferRef.current.length);
      if (batch.length === 0) return;
      fetch(`/api/browser-console/${encodeURIComponent(panelId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: batch }),
      }).catch(() => { /* silent-ok: console forwarding is best-effort telemetry */ });
    };
    const id = setInterval(flush, 1500);
    return () => { clearInterval(id); flush(); };
  }, [panelId]);

  const handleBack = useCallback(() => {
    const wv = webviewRef.current as any;
    if (isElectron && wv) wv.goBack?.();
  }, []);

  const handleForward = useCallback(() => {
    const wv = webviewRef.current as any;
    if (isElectron && wv) wv.goForward?.();
  }, []);

  // Hard refresh: reloadIgnoringCache bypasses ALL cache (HTML + CSS + JS)
  // while preserving cookies/login state. Falls back to destroy+recreate for iframes.
  function hardRefresh() {
    const wv = webviewRef.current as any;
    if (isElectron && wv?.reloadIgnoringCache) {
      wv.reloadIgnoringCache();
    } else {
      setReloadKey(k => k + 1);
    }
  }

  const handleReload = useCallback(() => {
    hardRefresh();
  }, []);

  // One-click bug report: capture the webview (best-effort), POST it with the
  // page context to /api/partner/bug-report → lands as inbox message for the
  // admin including the workspace's active conversation.
  const [bugState, setBugState] = useState<'idle' | 'sending' | 'ok' | 'err'>('idle');
  const handleReportBug = useCallback(async () => {
    if (bugState === 'sending') return;
    const note = window.prompt('Was ist das Problem? (kurz beschreiben, optional)');
    if (note === null) return; // Abbrechen = kein Report
    setBugState('sending');
    let screenshot: string | undefined;
    /*
     * WARUM KEIN BILD — das muss im Bericht stehen, sonst sucht jemand den falschen Fehler.
     *
     * Gemessen am 08.09.2026 auf dem Partner-Server: 46 von 47 Bug-Reports haben kein
     * Bild, und JEDER sagte "iframe-Fallback oder Capture-Fehler". Das klingt nach einem
     * Defekt im Fotografieren — es ist aber keiner. `capturePage` gibt es nur in der
     * Electron-App; die Partner arbeiten im Browser auf partner.werking.tools, dort
     * laeuft dieser Zweig nie. Ein Browser darf fremde Seiten in einem Rahmen auch gar
     * nicht abfotografieren, das ist keine Luecke, sondern die Sicherheitsgrenze.
     *
     * Der Bericht sagt jetzt, welcher der beiden Faelle vorlag. Damit wird auch die
     * echte Luecke sichtbar: wer im Browser arbeitet, kann bis heute KEIN Bild
     * mitschicken. Das zu aendern ginge nur serverseitig (Aufnahme ueber den
     * Shadow-Client) und ist bewusst nicht Teil dieser Aenderung.
     */
    let ohneBildGrund: 'kein-electron' | 'aufnahme-fehlgeschlagen' | undefined;
    const wv = webviewRef.current as any;
    try {
      if (isElectron && wv?.capturePage) {
        const img = await wv.capturePage();
        screenshot = img?.toDataURL?.();
        if (!screenshot) ohneBildGrund = 'aufnahme-fehlgeschlagen';
      } else {
        ohneBildGrund = 'kein-electron';
      }
    } catch {
      // silent-ok: Report ohne Screenshot ist besser als kein Report — aber nicht mehr
      // ohne Angabe, WARUM keins dabei ist.
      ohneBildGrund = 'aufnahme-fehlgeschlagen';
    }
    try {
      const res = await fetch('/api/partner/bug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ panelId, pageUrl: url, note, screenshot, ohneBildGrund }),
      });
      setBugState(res.ok ? 'ok' : 'err');
    } catch {
      setBugState('err');
    }
    setTimeout(() => setBugState('idle'), 3000);
  }, [bugState, panelId, url]);

  const handleDevTools = useCallback(() => {
    const wv = webviewRef.current as any;
    if (isElectron && wv) {
      // Use webview's built-in DevTools API (no IPC roundtrip needed)
      if (wv.isDevToolsOpened?.()) {
        wv.closeDevTools();
      } else {
        wv.openDevTools?.();
      }
    }
  }, []);

  // Cache-busted URL: only needed for iframe fallback (webviews use reloadIgnoringCache)
  function cacheBustedUrl(base: string, key: number): string {
    if (key === 0) return base;
    try {
      const u = new URL(base);
      u.searchParams.delete('_cb');
      u.searchParams.set('_cb', key.toString());
      return u.toString();
    } catch { // silent-ok: URL parse failure returns original base URL unchanged
      return base;
    }
  }

  const btnStyle = (enabled = true): React.CSSProperties => ({
    background: 'none',
    border: 'none',
    color: enabled ? 'var(--tn-text-muted)' : 'var(--tn-border)',
    cursor: enabled ? 'pointer' : 'default',
    fontSize: 13,
    padding: '1px 4px',
    borderRadius: 3,
  });

  // Device-viewport preview: frame dimensions + mobile UA.
  // NOTE: no CSS transform/scale on the frame — an Electron <webview> does not
  // tolerate a `transform` on any ancestor; it breaks the guest-viewport size
  // calculation (height collapses to the 150px replaced-element default → blank
  // below the fold). So the device renders at natural pixels and the content
  // area scrolls when the panel is smaller than the device.
  const device = DEVICES[deviceMode];
  const isDeviceView = deviceMode !== 'desktop';
  const FRAME_PAD = 12; // outer breathing room around the device
  const BEZEL = 10;     // black device bezel thickness (padding, not border)
  const frameW = device.w + BEZEL * 2;
  const frameH = device.h + BEZEL * 2;
  // Center when the device fits, else align to start so scrolling reaches every edge.
  const fitsH = contentSize.h === 0 || frameH + FRAME_PAD * 2 <= contentSize.h;
  const fitsW = contentSize.w === 0 || frameW + FRAME_PAD * 2 <= contentSize.w;
  // In device mode the webview reports the mobile UA; desktop keeps the sanitized UA.
  const googleSeite = istGoogleSeite(url);
  const activeUA = isDeviceView ? device.ua : googleSeite ? GOOGLE_LOGIN_UA : SANITIZED_UA;

  // Render the embedded page. `deviceMode` is part of the key so switching device
  // remounts the webview → the new useragent actually takes effect (Electron only
  // reads useragent at attach time).
  const renderMedia = (style: React.CSSProperties) => {
    if (!url) return null;
    return isElectron ? (
      <webview
        key={`wv-${deviceMode}-${googleSeite ? 'g' : 'n'}-${reloadKey}`}
        ref={webviewRef as any}
        src={url}
        partition={partition}
        {...(activeUA ? { useragent: activeUA } : {})}
        // Weiss wie das iframe unten und wie jeder Browser: ohne Grund ist die Webview
        // durchsichtig, und eine Seite ohne eigenen Hintergrund lag auf dem dunklen CUI-Grund
        // (gemessen 09.10.2026: 32 von 900 Buehnenseiten unlesbar, auf Weiss 3, keine schlechter).
        style={{ ...style, background: '#fff', pointerEvents: 'auto' }}
      />
    ) : (
      <iframe
        key={`if-${deviceMode}-${reloadKey}`}
        ref={iframeRef}
        src={cacheBustedUrl(liveProxyRewrite(url), reloadKey)}
        onLoad={handleIframeLoad}
        style={{ ...style, background: '#fff' }}
      />
    );
  };

  // Detect which Werkingflow app the webview shows, for the login add-in.
  const festErkannt = isElectron ? detectApp(url) : null;
  // Partner-Sandkasten (Rafael, 29.09.2026): unbekannter Port des eigenen Hauses →
  // der CUI-Server fragt die App nach /api/version. Nur wenn die feste Erkennung
  // nichts findet; 3008/Staging/Prod bleiben wie sie sind. Immer dev-Satz.
  const sandkastenPort = isElectron && !festErkannt ? sandkastenPortAusUrl(url) : null;
  const sandkastenAppKey = useSandkastenApp(sandkastenPort);
  const detected = festErkannt ?? (sandkastenPort !== null ? detectSandkastenApp(url, sandkastenAppKey) : null);
  // The local/partner URL for the detected app, so the login add-in's Local/Prod
  // toggle can navigate back to the local instance (the prod direction uses the
  // app's productionUrl, which the add-in already has from /api/app-credentials).
  // Im Sandkasten ist "lokal" der Sandkasten selbst, nicht der feste Port der App.
  const detectedDevPort = detected ? (detected.isSandbox ? detected.port : getDevPort(detected.appKey)) : null;
  const localAppUrl = detectedDevPort !== null ? devPortUrl(detectedDevPort, '/') : null;
  // Ausserhalb von Electron (iPad-Safari, Browser; Rafael 05.10.2026): dieselbe
  // Erkennung auf die bekannte Adresse `url` — die iframe-Adresse selbst ist
  // seitenfremd und nicht lesbar. Statt Anmeldung nur eine Ausfuell-Hilfe.
  const browserErkannt = !isElectron && url ? detectApp(url) : null;
  const zeigtPartnerCui = isElectron && isPartnerCui(url);
  // Session switcher for a panel showing the partner CUI itself. Admin-only —
  // but mirroring requireRole(), which lets everything through when auth is
  // OFF: the dev CUI runs authEnabled:false, so there is no user object there
  // and a bare role check would hide the switcher exactly where it is used.
  // The real boundary is the partner server's own gate on /api/auth/users +
  // /impersonate; this only keeps the affordance out of partner UIs.
  //
  // A tile with `konto` gets the AUTOMATIC login (PartnerKontoAutoLogin,
  // rendered below) instead — the manual switcher and the rezept-driven
  // login would otherwise race for the same webview.
  const showSessionSwitcher = zeigtPartnerCui && (!authEnabled || user?.role === 'admin') && !kontoName;

  return (
    <div ref={containerRef} className="browser-panel-interactive" style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--tn-surface)', position: 'relative', userSelect: 'text' }}>
      <div
        data-ai-id="browser-panel-adresszeile"
        style={{
          display: adresszeileZu ? 'none' : 'flex',
          alignItems: 'center',
          gap: 4,
          // In Buehnen eine Spur flacher: dort stehen Kopfleiste und Reiter schon darueber.
          padding: inBuehne ? '1px 6px' : '4px 8px',
          background: 'var(--tn-bg-dark)',
          borderBottom: '1px solid var(--tn-border)',
          height: inBuehne ? 26 : 30,
          flexShrink: 0,
        }}
      >
        {isElectron && (
          <>
            <button data-ai-id="browser-panel-back" onClick={handleBack} style={btnStyle(canGoBack)} disabled={!canGoBack} title="Back">&#8592;</button>
            <button data-ai-id="browser-panel-forward" onClick={handleForward} style={btnStyle(canGoForward)} disabled={!canGoForward} title="Forward">&#8594;</button>
          </>
        )}
        <button data-ai-id="browser-panel-hard-refresh" onClick={handleReload} style={btnStyle()} title="Hard Refresh (Cache loeschen)">&#8635;</button>
        {!zeigeTabKnopf && (
          <button data-ai-id="browser-panel-open-new-tab" onClick={openInNewTab} style={btnStyle(!!url)} disabled={!url} title="In neuem Tab oeffnen">&#8599;</button>
        )}
        <input
          data-ai-id="browser-panel-url-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(); }}
          placeholder="URL or search..."
          style={{
            flex: 1,
            background: 'var(--tn-bg)',
            color: 'var(--tn-text)',
            border: '1px solid var(--tn-border)',
            borderRadius: 4,
            padding: '3px 8px',
            fontSize: 11,
          }}
        />
        <button
          data-ai-id="browser-panel-go"
          onClick={navigate}
          style={{
            background: 'var(--tn-border)',
            border: 'none',
            color: 'var(--tn-text)',
            padding: '3px 10px',
            borderRadius: 4,
            fontSize: 11,
            cursor: 'pointer',
          }}
        >
          Go
        </button>
        {zeigeTabKnopf && (
          <button
            type="button"
            data-ai-id="browser-panel-open-new-tab"
            onClick={openInNewTab}
            title="Seite im eigenen Browser-Tab öffnen — dort hält die Anmeldung auch auf dem iPad"
            style={{
              background: 'var(--tn-bg)',
              border: '1px solid var(--tn-border)',
              color: 'var(--tn-text)',
              padding: '0 10px',
              minHeight: inBuehne ? 22 : 24,
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              touchAction: 'manipulation',
              flexShrink: 0,
            }}
          >
            Im eigenen Tab öffnen &#8599;
          </button>
        )}
        <button
          data-ai-id="browser-panel-report-bug"
          onClick={handleReportBug}
          style={{
            ...btnStyle(bugState !== 'sending'),
            color: bugState === 'ok' ? 'var(--tnf-4ade80)' : bugState === 'err' ? 'var(--tnf-f87171)' : 'var(--tn-text-muted)',
          }}
          title="Problem melden — Screenshot + Kontext gehen als Inbox-Nachricht an den Admin"
        >
          {bugState === 'ok' ? '✓' : bugState === 'err' ? '✗' : bugState === 'sending' ? '…' : '🐞'}
        </button>
        {isElectron && (
          <button data-ai-id="browser-panel-devtools" onClick={handleDevTools} style={btnStyle()} title="DevTools">
            {'</>'}
          </button>
        )}
        <div style={{ display: 'flex', gap: 2, marginLeft: 2 }}>
          {(Object.keys(DEVICES) as DeviceMode[]).map((m) => (
            <button
              key={m}
              data-ai-id={`browser-panel-device-${m}`}
              onClick={() => applyDevice(m)}
              title={`Ansicht: ${DEVICES[m].label}${DEVICES[m].w ? ` (${DEVICES[m].w}×${DEVICES[m].h})` : ''}`}
              style={{
                ...btnStyle(),
                fontSize: 13,
                borderRadius: 3,
                background: deviceMode === m ? 'var(--tn-border)' : 'none',
                color: deviceMode === m ? 'var(--tn-text)' : 'var(--tn-text-muted)',
              }}
            >
              {DEVICES[m].icon}
            </button>
          ))}
        </div>
        <span ref={setLoginPlatz} data-ai-id="browser-panel-login-platz" style={{ display: 'flex', marginLeft: 2 }} />
      </div>
      {kontoOhneBox && url && (
        <div
          data-ai-id="browser-panel-konto-ohne-box"
          style={{
            padding: '4px 10px',
            fontSize: 11,
            lineHeight: 1.4,
            background: 'var(--tn-bg-dark)',
            color: 'var(--tn-text-muted)',
            borderBottom: '1px solid var(--tn-border)',
            flexShrink: 0,
          }}
        >
          Ohne automatische Anmeldung als <strong>{kontoName}</strong>: hier gilt deine eigene
          Anmeldung in diesem Browser. Steht eine Login-Maske da, melde dich selbst an.
        </div>
      )}
      <div
        ref={contentRef}
        style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          alignItems: !isDeviceView ? 'stretch' : (fitsH ? 'center' : 'flex-start'),
          justifyContent: !isDeviceView ? 'center' : (fitsW ? 'center' : 'flex-start'),
          overflow: 'auto',
          background: isDeviceView ? 'var(--tn-bg-dark)' : 'var(--tn-surface)',
          padding: isDeviceView ? FRAME_PAD : 0,
        }}
      >
        {ladeFehler && (
          <div
            role="alert"
            data-ai-id="browser-panel-ladefehler"
            style={{
              position: 'absolute', inset: 0, zIndex: 5, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24, textAlign: 'center',
              background: 'var(--tn-surface)', color: 'var(--tn-text)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>Seite konnte nicht geladen werden</div>
            <div style={{ fontSize: 12, opacity: 0.75, maxWidth: 460, lineHeight: 1.5, wordBreak: 'break-all' }}>
              {ladeFehler.text} ({ladeFehler.code}) — {ladeFehler.adresse}
              {nachlade ? '' : ladeFehler.laedt ? ' · lädt erneut …' : ladeFehler.neuVersuch ? ' · ein neuer Versuch läuft automatisch' : ''}
              {ladeFehler.reloadFehler ? ` · Neu laden gescheitert: ${ladeFehler.reloadFehler}` : ''}
            </div>
            {nachlade && (
              <div data-ai-id="browser-panel-nachladen-status" style={{ fontSize: 11, opacity: 0.75 }}>
                {`Verbindung fehlgeschlagen (${nachlade.code}), `}
                {ladeFehler.laedt
                  ? `Versuch ${nachlade.versuch} läuft …`
                  : nachlade.naechsterVersuchUm === null
                    ? 'nächster Versuch, sobald das Fenster sichtbar ist'
                    : `nächster Versuch in ${Math.max(0, Math.ceil((nachlade.naechsterVersuchUm - uhrTick) / 1000))} s`}
              </div>
            )}
            <button
              data-ai-id="browser-panel-ladefehler-neu-laden"
              onClick={() => {
                const ersatz = rueckrufeRef.current.onNeuLaden;
                if (ersatz) ersatz();
                else if (!(nachlade && planerRef.current?.sofort('knopf'))) nachLadefehlerNeuLaden(webviewRef.current);
              }}
              style={{ background: 'var(--tn-accent, var(--tn-link))', border: 'none', color: 'var(--tn-bg)', padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              {nachlade ? 'Jetzt neu laden ↻' : 'Neu laden ↻'}
            </button>
          </div>
        )}
        {embedBlocked && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: 24,
              textAlign: 'center',
              background: 'var(--tn-surface)',
              color: 'var(--tn-text)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>Diese Seite lässt sich hier nicht anzeigen</div>
            <div style={{ fontSize: 12, opacity: 0.75, maxWidth: 420, lineHeight: 1.5 }}>
              {(() => { try { return new URL(url).hostname; } catch { return 'Die Seite'; } })()} erlaubt es
              nicht, sich in eine andere Seite einbetten zu lassen. Das ist eine Vorgabe der Seite selbst —
              im neuen Tab funktioniert sie normal.
            </div>
            <button
              data-ai-id="browser-panel-embed-blocked-open-new-tab"
              onClick={openInNewTab}
              style={{
                background: 'var(--tn-accent, var(--tn-link))',
                border: 'none',
                color: 'var(--tn-bg)',
                padding: '6px 14px',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              In neuem Tab öffnen ↗
            </button>
          </div>
        )}
        {url ? (
          isDeviceView ? (
            // Device frame at NATURAL pixels (no transform — see note above).
            // The frame is a fixed-size flex column and the webview fills it via
            // flex:1 — an Electron <webview> takes its size from flex sizing
            // reliably, but NOT from a raw height:852px / height:100% in a block
            // (there it falls back to the 150px replaced-element default). This
            // mirrors the working desktop path (flex:1 in a stretch container).
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: frameW,
                height: frameH,
                boxSizing: 'border-box',
                padding: BEZEL,
                borderRadius: 26,
                overflow: 'hidden',
                boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                background: 'var(--tnfh-111111)',
                flexShrink: 0,
              }}
            >
              {renderMedia({ flex: 1, width: '100%', minHeight: 0, border: 'none', borderRadius: 16 })}
            </div>
          ) : (
            renderMedia({ flex: 1, border: 'none', width: '100%', minHeight: 0 })
          )
        ) : (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              color: 'var(--tn-text-muted)',
              fontSize: 13,
            }}
          >
            <span style={{ fontSize: 24 }}>
              {isElectron ? 'Electron Browser' : 'Browser'}
            </span>
            <span>URL eingeben oder suchen</span>
            {!isElectron && (
              <span style={{ fontSize: 10, opacity: 0.6 }}>
                Electron-App starten fuer DevTools + volle Browser-Funktionalitaet
              </span>
            )}
          </div>
        )}
      </div>
      {isElectron && inBuehne && buehnenKennung && kontoName && appPortZiel(initialUrl) && (
        <AppPortGateBootstrap ziel={initialUrl} konto={kontoName} partition={partition} buehne={buehnenKennung} panelId={panelId ?? ''} mensch={user?.id ?? null} aufnahme={user?.id === 'shadow-client' || user?.id === 'aufnahme'} webviewRef={webviewRef} />
      )}
      {detected && (
        <AppLoginPanel
          appKey={detected.appKey}
          isLoginPage={detected.isLoginPage}
          isProduction={detected.isProduction}
          credentialSet={detected.credentialSet}
          localUrl={localAppUrl}
          sandkastenPort={detected.isSandbox ? detected.port : null}
          webviewRef={webviewRef}
          konto={kontoName}
          isStaging={detected.isStaging}
          adresszeilenPlatz={adresszeileZu ? null : loginPlatz}
        />
      )}
      {browserErkannt && (
        <AppLoginHilfe
          appKey={browserErkannt.appKey}
          credentialSet={browserErkannt.credentialSet}
          konto={kontoName}
          isStaging={browserErkannt.isStaging}
        />
      )}
      {zeigtPartnerCui && kontoName && !appPortZiel(initialUrl) && (
        <PartnerKontoAutoLogin konto={kontoName} url={isPartnerCui(initialUrl) ? initialUrl : url} partition={partition} aufnahme={user?.id === 'shadow-client'} webviewRef={webviewRef} />
      )}
      {showSessionSwitcher && <PartnerSessionPanel webviewRef={webviewRef} />}
      {ausfuellen !== undefined && (
        <Vorbefuellung rezept={ausfuellen} isElectron={isElectron} containerRef={containerRef} webviewRef={webviewRef} iframeRef={iframeRef} />
      )}
    </div>
  );
}
